// CSS shorthand(`background`/`border`·`border-{side}`/`border-color`) 안에 `var()` 가 섞이면
// 브라우저는 해당 shorthand 의 모든 longhand 를 pending-substitution 으로 만들어
// `getPropertyValue(<longhand>)` 가 "" 를 돌려준다. 이때 원본 shorthand 문자열을 복구해
// color 토큰만 뽑아내기 위한 유틸. 분류는 공백 split 이 아니라 postcss-value-parser 토큰 트리로 한다.
import valueParser from 'postcss-value-parser'

export type BorderSide = 'top' | 'right' | 'bottom' | 'left'

type PvNode = ReturnType<typeof valueParser>['nodes'][number]
type PvFunctionNode = Extract<PvNode, { type: 'function' }>
type BorderTokenType = 'width' | 'style' | 'color' | 'image' | 'unknown'

// border-style 키워드(유한집합) — 나머지 word 는 색으로 확정하는 근거.
const BORDER_STYLE_KEYWORDS = new Set([
  'none',
  'hidden',
  'dotted',
  'dashed',
  'solid',
  'double',
  'groove',
  'ridge',
  'inset',
  'outset',
])

const BORDER_WIDTH_KEYWORDS = new Set(['thin', 'medium', 'thick'])

const COLOR_FUNCTIONS = new Set([
  'rgb',
  'rgba',
  'hsl',
  'hsla',
  'hwb',
  'lab',
  'lch',
  'oklab',
  'oklch',
  'color',
  'color-mix',
])

const WIDTH_FUNCTIONS = new Set(['calc', 'min', 'max', 'clamp'])

// `background` 레이어에서 color 가 아님이 확실한 키워드(repeat/position/attachment/box/size).
const BACKGROUND_NON_COLOR_KEYWORDS = new Set([
  'repeat',
  'repeat-x',
  'repeat-y',
  'no-repeat',
  'space',
  'round',
  'fixed',
  'scroll',
  'local',
  'border-box',
  'padding-box',
  'content-box',
  'center',
  'top',
  'bottom',
  'left',
  'right',
  'cover',
  'contain',
  'auto',
  'none',
])

// 숫자+(선택)길이단위. 단위 없는 0 도 포함.
const LENGTH_RE = /^[\d.]+(px|em|rem|pt|%|vh|vw|vmin|vmax|cm|mm|in|pc|ex|ch|q|fr)?$/i
// linear-/radial-/conic-/repeating-*-gradient.
const GRADIENT_FN_RE = /gradient$/

const isMeaningful = (node: PvNode): boolean =>
  node.type !== 'space' && node.type !== 'div' && node.type !== 'comment'

// `var(key, fallback)` 의 첫 depth-0 콤마 뒤 첫 유효 토큰(= fallback 의 머리) 반환. 없으면 null.
function varFallbackNode(fn: PvFunctionNode): PvNode | null {
  let sawComma = false
  for (const node of fn.nodes) {
    if (!sawComma) {
      if (node.type === 'div' && node.value === ',') sawComma = true
      continue
    }
    if (node.type === 'space' || node.type === 'comment') continue
    return node
  }
  return null
}

function tokenType(node: PvNode): BorderTokenType {
  if (node.type === 'word') {
    const lower = node.value.toLowerCase()
    if (BORDER_STYLE_KEYWORDS.has(lower)) return 'style'
    if (BORDER_WIDTH_KEYWORDS.has(lower)) return 'width'
    if (LENGTH_RE.test(node.value)) return 'width'
    return 'color'
  }
  if (node.type === 'function') {
    const name = node.value.toLowerCase()
    if (COLOR_FUNCTIONS.has(name)) return 'color'
    if (WIDTH_FUNCTIONS.has(name)) return 'width'
    if (name === 'url' || name === 'image' || GRADIENT_FN_RE.test(name)) return 'image'
    if (name === 'var') {
      const fallback = varFallbackNode(node)
      return fallback == null ? 'unknown' : tokenType(fallback)
    }
    return 'unknown'
  }
  return 'unknown'
}

// border/border-{side} shorthand value 문자열에서 color 토큰만 추출. var/중첩/순서 모두 안전.
export function extractBorderColor(value: string): string | null {
  const tokens = valueParser(value).nodes.filter(isMeaningful)
  const slot: Record<'width' | 'style' | 'color', PvNode | null> = {
    width: null,
    style: null,
    color: null,
  }
  const unknowns: PvNode[] = []

  for (const node of tokens) {
    const type = tokenType(node)
    if (type === 'color') {
      if (slot.color == null) slot.color = node
    } else if (type === 'style') {
      if (slot.style == null) slot.style = node
    } else if (type === 'width') {
      if (slot.width == null) slot.width = node
    } else if (type === 'unknown') {
      unknowns.push(node)
    }
    // 'image' 는 border 에 올 수 없으므로 무시.
  }

  // fallback 없는 var 등 미확정 토큰은 빈 슬롯에 color 우선 배정.
  for (const node of unknowns) {
    if (slot.color == null) slot.color = node
    else if (slot.width == null) slot.width = node
    else if (slot.style == null) slot.style = node
  }

  return slot.color == null ? null : valueParser.stringify(slot.color)
}

// border-color box(1/2/3/4 value)를 면별 color 로 전개(각 원형 보존, 없으면 null).
export function extractBorderColorBox(value: string): Record<BorderSide, string | null> {
  const values = valueParser(value)
    .nodes.filter(isMeaningful)
    .map((node) => valueParser.stringify(node))

  if (values.length === 1) {
    const [a] = values
    return { top: a, right: a, bottom: a, left: a }
  }
  if (values.length === 2) {
    const [a, b] = values
    return { top: a, right: b, bottom: a, left: b }
  }
  if (values.length === 3) {
    const [a, b, c] = values
    return { top: a, right: b, bottom: c, left: b }
  }
  if (values.length >= 4) {
    const [a, b, c, d] = values
    return { top: a, right: b, bottom: c, left: d }
  }
  return { top: null, right: null, bottom: null, left: null }
}

// `background` 레이어의 한 토큰이 color 후보면 그 노드를, 아니면 null.
function backgroundColorNode(node: PvNode): PvNode | null {
  if (node.type === 'word') {
    const lower = node.value.toLowerCase()
    if (BACKGROUND_NON_COLOR_KEYWORDS.has(lower)) return null
    if (LENGTH_RE.test(node.value)) return null
    return node
  }
  if (node.type === 'function') {
    const name = node.value.toLowerCase()
    if (COLOR_FUNCTIONS.has(name)) return node
    if (name === 'var') {
      const fallback = varFallbackNode(node)
      // fallback 이 color 로 분류되거나(중첩 색) fallback 이 없는 bare var 면 color 후보로 본다.
      // bare var 는 _getDarkModeColor 에서 변환 대상 fallback 이 없어 no-op 이 되므로 무해하다.
      if (fallback == null) return node
      return backgroundColorNode(fallback) == null ? null : node
    }
    return null
  }
  return null
}

// `background` shorthand value 에서 background-color 추출. top-level 콤마 레이어 중 마지막 레이어만.
export function extractBackgroundColor(value: string): string | null {
  const nodes = valueParser(value).nodes
  let lastLayerStart = 0
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if (node.type === 'div' && node.value === ',') lastLayerStart = i + 1
  }

  for (let i = lastLayerStart; i < nodes.length; i++) {
    const node = nodes[i]
    if (!isMeaningful(node)) continue
    const colorNode = backgroundColorNode(node)
    if (colorNode != null) return valueParser.stringify(colorNode)
  }
  return null
}

// box-shadow/text-shadow 처럼 "offset·blur·spread·color·inset" 토큰이 공백/콤마로 나열되는 값에서
// 각 top-level 토큰(공백/콤마/주석 제외)을 convertColor 에 넘기고, non-null 이면 치환·null 이면 원본
// 유지한 뒤 공백·콤마 구분자를 그대로 보존해 재조립한다. 색 판별은 convertColor(= chroma 파싱) 결과의
// null 여부로 위임한다(길이·inset·none 등 비색 토큰은 null 로 떨어져 보존됨).
export function mapBoxShadowColors(
  value: string,
  convertColor: (token: string) => string | null,
): string {
  let result = ''
  for (const node of valueParser(value).nodes) {
    if (!isMeaningful(node)) {
      result += valueParser.stringify(node)
      continue
    }
    const original = valueParser.stringify(node)
    result += convertColor(original) ?? original
  }
  return result
}

function enumerateStyle(
  style: CSSStyleDeclaration,
  enumerated?: readonly string[],
): readonly string[] {
  return enumerated ?? Array.from(style)
}

// CSSOM 선검사(getPropertyValue + Array.from 열거) → 소스복구 → 파싱. 반환값은 원형 color 문자열 또는 null.
// enumerated 는 요소당 1회 생성한 Array.from(style) 를 재사용하기 위한 선택 인자(hot-path 최적화).
export function resolveBackgroundColor(
  style: CSSStyleDeclaration,
  enumerated?: readonly string[],
): string | null {
  const direct = style.getPropertyValue('background-color')
  if (direct.length > 0) return direct

  if (enumerateStyle(style, enumerated).indexOf('background-color') < 0) return null

  return extractBackgroundColor(style.getPropertyValue('background'))
}

// side 의 border color 를 복구. 복구 순서: border-{side}-color → border-{side} → border-color(box) → border.
export function resolveBorderColor(
  style: CSSStyleDeclaration,
  side: BorderSide,
  box?: Record<BorderSide, string | null>,
  enumerated?: readonly string[],
): string | null {
  const prop = `border-${side}-color`
  const direct = style.getPropertyValue(prop)
  if (direct.length > 0) return direct

  if (enumerateStyle(style, enumerated).indexOf(prop) < 0) return null

  const sideShorthand = style.getPropertyValue(`border-${side}`)
  if (sideShorthand.length > 0) {
    const fromSide = extractBorderColor(sideShorthand)
    if (fromSide != null) return fromSide
  }

  const resolvedBox = box ?? extractBorderColorBox(style.getPropertyValue('border-color'))
  if (resolvedBox[side] != null) return resolvedBox[side]

  const borderShorthand = style.getPropertyValue('border')
  if (borderShorthand.length > 0) {
    const fromBorder = extractBorderColor(borderShorthand)
    if (fromBorder != null) return fromBorder
  }

  return null
}
